import { Container, Graphics, Rectangle, Text, type FederatedPointerEvent } from 'pixi.js';
import {
  createTextLayoutDebugEntry,
  fitTextToBounds,
  type TextLayoutDebugEntry
} from './TextLayoutDebug';
import { UI_COLORS, UI_TEXT_STYLES } from './Theme';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type ButtonState = 'idle' | 'hover' | 'pressed' | 'disabled';
export type ButtonPressSoundHandler = () => void;

export interface ButtonOptions {
  label: string;
  width?: number;
  height?: number;
  variant?: ButtonVariant;
  disabled?: boolean;
  playPressSound?: boolean;
  pressSound?: ButtonPressSoundHandler;
  onPress?: () => void;
}

export interface ButtonDisabledOptions {
  muted?: boolean;
}

interface ButtonPalette {
  fill: number;
  stroke: number;
  text: number;
}

const BUTTON_PALETTES: Record<ButtonVariant, ButtonPalette> = {
  primary: {
    fill: UI_COLORS.redDark,
    stroke: UI_COLORS.gold,
    text: UI_COLORS.ink
  },
  secondary: {
    fill: UI_COLORS.paperLight,
    stroke: UI_COLORS.gold,
    text: UI_COLORS.ink
  },
  danger: {
    fill: UI_COLORS.red,
    stroke: UI_COLORS.ink,
    text: UI_COLORS.ink
  },
  ghost: {
    fill: UI_COLORS.black,
    stroke: UI_COLORS.mutedInk,
    text: UI_COLORS.ink
  }
};

export class Button extends Container {
  private static defaultPressSound: ButtonPressSoundHandler | undefined;

  private readonly background = new Graphics();
  private readonly labelText = new Text({
    text: '',
    style: UI_TEXT_STYLES.button
  });

  private buttonWidth: number;
  private buttonHeight: number;
  private variant: ButtonVariant;
  private state: ButtonState = 'idle';
  private disabledMuted = true;
  private playPressSound: boolean;
  private pressSound?: ButtonPressSoundHandler;
  private onPress?: () => void;

  public static setDefaultPressSound(handler: ButtonPressSoundHandler | undefined): void {
    Button.defaultPressSound = handler;
  }

  public constructor(options: ButtonOptions) {
    super();
    this.buttonWidth = options.width ?? 260;
    this.buttonHeight = options.height ?? 56;
    this.variant = options.variant ?? 'secondary';
    this.playPressSound = options.playPressSound ?? true;
    this.pressSound = options.pressSound;
    this.onPress = options.onPress;

    this.addChild(this.background);
    this.addChild(this.labelText);
    this.labelText.anchor.set(0.5);

    this.on('pointerover', this.handlePointerOver);
    this.on('pointerout', this.handlePointerOut);
    this.on('pointerdown', this.handlePointerDown);
    this.on('pointerupoutside', this.handlePointerUpOutside);
    this.on('pointertap', this.handlePointerTap);

    this.setLabel(options.label);
    this.setDisabled(options.disabled ?? false);
  }

  public setLabel(label: string): void {
    this.labelText.text = label;
    this.layout();
  }

  public setVariant(variant: ButtonVariant): void {
    this.variant = variant;
    this.draw();
  }

  public setSize(width: number, height: number): void {
    this.buttonWidth = width;
    this.buttonHeight = height;
    this.hitArea = new Rectangle(0, 0, width, height);
    this.layout();
    this.draw();
  }

  public setDisabled(disabled: boolean, options: ButtonDisabledOptions = {}): void {
    this.state = disabled ? 'disabled' : 'idle';
    this.disabledMuted = disabled ? (options.muted ?? true) : false;
    this.eventMode = disabled ? 'none' : 'static';
    this.cursor = disabled ? 'default' : 'pointer';
    this.alpha = disabled && this.disabledMuted ? 0.58 : 1;
    this.hitArea = new Rectangle(0, 0, this.buttonWidth, this.buttonHeight);
    this.draw();
  }

  public setOnPress(onPress: (() => void) | undefined): void {
    this.onPress = onPress;
  }

  public setPressSound(pressSound: ButtonPressSoundHandler | undefined): void {
    this.pressSound = pressSound;
  }

  public getTextLayoutDebugEntry(id = 'button.label'): TextLayoutDebugEntry {
    return createTextLayoutDebugEntry(
      id,
      this.labelText,
      getButtonLabelMaxWidth(this.buttonWidth),
      getButtonLabelMaxHeight(this.buttonHeight)
    );
  }

  private readonly handlePointerOver = (): void => {
    if (this.state !== 'disabled') {
      this.state = 'hover';
      this.draw();
    }
  };

  private readonly handlePointerOut = (): void => {
    if (this.state !== 'disabled') {
      this.state = 'idle';
      this.draw();
    }
  };

  private readonly handlePointerDown = (): void => {
    if (this.state !== 'disabled') {
      this.state = 'pressed';
      this.draw();
    }
  };

  private readonly handlePointerUpOutside = (): void => {
    if (this.state !== 'disabled') {
      this.state = 'idle';
      this.draw();
    }
  };

  private readonly handlePointerTap = (event: FederatedPointerEvent): void => {
    event.stopPropagation();
    if (this.state === 'disabled') {
      return;
    }

    this.state = 'hover';
    this.draw();
    if (!this.onPress) {
      return;
    }

    if (this.playPressSound) {
      (this.pressSound ?? Button.defaultPressSound)?.();
    }
    this.onPress();
  };

  private layout(): void {
    this.labelText.style.align = 'center';
    fitTextToBounds(this.labelText, {
      maxWidth: getButtonLabelMaxWidth(this.buttonWidth),
      maxHeight: getButtonLabelMaxHeight(this.buttonHeight),
      baseFontSize: 22,
      minFontSize: 13,
      baseLineHeight: 26,
      minLineHeight: 16,
      wordWrap: true,
      breakWords: true
    });
    this.labelText.position.set(this.buttonWidth / 2, this.buttonHeight / 2 - 1);
  }

  private draw(): void {
    const palette = BUTTON_PALETTES[this.variant];
    const stateBoost = this.state === 'hover' ? 0.12 : this.state === 'pressed' ? -0.08 : 0;
    const fillAlpha = this.variant === 'ghost' ? 0.42 + stateBoost : 0.9 + stateBoost;
    const strokeAlpha = this.state === 'pressed' ? 1 : 0.78;
    const offsetY = this.state === 'pressed' ? 2 : 0;

    this.background.clear();
    this.background
      .roundRect(0, offsetY, this.buttonWidth, this.buttonHeight - offsetY, 6)
      .fill({ color: palette.fill, alpha: Math.max(0.2, Math.min(fillAlpha, 1)) })
      .stroke({ color: palette.stroke, width: 2, alpha: strokeAlpha });
    this.background
      .rect(10, offsetY + 8, 4, this.buttonHeight - 16)
      .fill({ color: palette.stroke, alpha: 0.72 });
    this.labelText.style.fill =
      this.state === 'disabled' && this.disabledMuted ? UI_COLORS.disabled : palette.text;
  }
}

function getButtonLabelMaxWidth(width: number): number {
  return Math.max(1, width - 30);
}

function getButtonLabelMaxHeight(height: number): number {
  return Math.max(1, height - 12);
}
